import React, { useEffect, useState } from 'react';
import { useIntercom } from 'react-use-intercom';

import { EXTERNAL_APP_CONFIGS } from '@hcs/app-configs';
import { useAccount, useUserHmacHash } from '@hcs/authn';
import { useAppAccessCheckMap } from '@hcs/authz';
import { useIsHelpChatEnabled } from '@hcs/help-chat';
import { useOrgSubscriptionType } from '@hcs/self-serve-info';
import { capitalizeFirstLetterOfEachWord, formatKebabCase } from '@hcs/utils';

const HelpChatUserActiveIntercom = () => {
  const [wasUserLoggedIn, setWasUserLoggedIn] = useState(false);
  const { data: account } = useAccount();
  const { data: appAccessCheckMap } = useAppAccessCheckMap();
  const { shutdown, update, boot } = useIntercom();
  const { data: subscriptionType } = useOrgSubscriptionType();
  const { user, organization } = account || {};
  const { data: hmacData } = useUserHmacHash();

  const { hmac } = hmacData || {};

  // update user when account loads
  useEffect(() => {
    if (user && hmac && subscriptionType && appAccessCheckMap) {
      // Intercom subscription types have caplitalized letters. This code is for consistency w/ what is already in intercom.
      const intercomSubscriptionType = capitalizeFirstLetterOfEachWord(
        formatKebabCase(subscriptionType),
      )
        .split(' ')
        .join('-');
      update({
        email: user.email,
        userId: user.id.toString(),
        userHash: hmac,
        name:
          user.firstName && user.lastName
            ? `${user.firstName} ${user.lastName}`
            : undefined,
        company: organization
          ? {
              companyId: organization.id.toString(),
              name: organization.slug,
              plan: EXTERNAL_APP_CONFIGS.filter((appConfig) => {
                if (
                  appConfig.type === 'product' &&
                  appAccessCheckMap[appConfig.applicationKey]
                ) {
                  return true;
                }
                return false;
              })
                .map((appConfig) => appConfig.applicationKey)
                .sort()
                .join(', '),
              customAttributes: {
                orgName: organization.name,
                salesforceAccountId: organization.salesforceAccountId,
                subscriptionType: intercomSubscriptionType,
              },
            }
          : undefined,
        createdAt: user.createdOn,
      });
      setWasUserLoggedIn(true);
    }
  }, [user, organization, subscriptionType, hmac, update, appAccessCheckMap]);

  // clear user when account is cleared
  useEffect(() => {
    if (!user?.email && wasUserLoggedIn) {
      shutdown();
      // re-boot so help chat is available on login / if the user logs back in
      boot();
      setWasUserLoggedIn(false);
    }
  }, [user, wasUserLoggedIn, shutdown, boot]);

  return null;
};

export const IntercomUser = () => {
  const isHelpChatEnabled = useIsHelpChatEnabled();
  if (isHelpChatEnabled) {
    return <HelpChatUserActiveIntercom />;
  }
  return null;
};
